/* eslint-disable @typescript-eslint/no-explicit-any */
import VerificationTile from '@components/verification-tile';
import { useSpecialConditions } from '@config/specialConditions';
import { StartingProcessDialogType } from '@containers/form-verification/form/types';
import { WES_ORG } from '@global/constants/general';
import { INSUFF_CATEGORY, TxnState } from '@global/constants/verification';
import { isClientCareerMosaic } from '@global/helpers/general';
import { mixpanel, trackingEvents } from '@global/monitoring/mixpanel';
import { VerificationState } from '@global/types/form';
import { AccountBalance } from '@mui/icons-material';
import UserIcon from '@mui/icons-material/PermIdentity';
import { Box, Typography } from '@mui/material';
import { RootState } from '@redux/store';
import { navigateTo } from '@services/navigation';
import { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { basicDetailsReqd } from '../helpers';
import { verificationTileOnClick } from './helpers';

function VerificationTiles(props: {
  setOpenStartingProcessDialog: React.Dispatch<React.SetStateAction<StartingProcessDialogType>>
}) {
  const { setOpenStartingProcessDialog } = props;
  const profile = useSelector((s: any) => s.profile);
  const formData = useSelector((s: any) => s.formData);
  const { envCondition } = useSpecialConditions();
  const { searchedQuery } = useSelector((s: RootState) => s.misc);
  const misc = useSelector((s: any) => s.misc);
  const { startButtonClicked } = misc ?? {};
  const { orgInfo = {}, txnInfo = {}, verificationsByCategory = {} } = formData ?? {};
  const profileVerified = profile?.profileDetails?.name ? true : false;
  const profileNotVerified = txnInfo?.manualProfileRequired === false && !profileVerified;
  const { disclaimerAgreed } = useSelector((s: RootState) => s.misc);
  const showBasicDetails = basicDetailsReqd(txnInfo);

  const getDisabledForVerifications = (state: string, markedAsSkip: boolean) => {
    return (
      profileNotVerified ||
      (txnInfo?.state === TxnState.NOT_INITIALIZED && showBasicDetails) ||
      (txnInfo?.form?.isExpired && state === TxnState.NOT_INITIALIZED) ||
      envCondition.isModificationAllowed({ markedAsSkip })
    );
  };

  const getState = (state: string, markedAsSkip: boolean) => {
    if (txnInfo?.state === TxnState.NOT_INITIALIZED) {
      return '';
    }
    if (markedAsSkip) {
      return TxnState.SKIPPED;
    } else {
      return state as unknown as TxnState;
    }
  };

  const showPurposesDialog = (fn: VoidFunction) => {
    if (
      (txnInfo?.state === TxnState.NOT_INITIALIZED &&
        startButtonClicked !== searchedQuery?.txnId) ||
      txnInfo?.questionnaire?.isRequired
    ) {
      setOpenStartingProcessDialog({
        callback: async () => {
          fn();
        }
      });
    } else {
      fn();
    }
  };

  const getSubtitle = (verificationName: string) => {
    switch (verificationName) {
      case 'bgv-degree':
        return '(All institutes across India)';
      case 'bgv-tenth':
        return '(All state and central boards)';
      case 'bgv-twelfth':
        return '(All state and central boards)';
      default:
        return '';
    }
  };

  const isDisclaimerChecked = () => {
    const txnId = txnInfo?._id || searchedQuery?.txnId;
    const showDisclaimer =
      import.meta.env.VITE_SHOW_DISCLAIMER &&
      !disclaimerAgreed?.[txnId] &&
      txnInfo?.state === TxnState.NOT_INITIALIZED;

    if (showDisclaimer) {
      return false;
    } else {
      return true;
    }
  };

  return (
    <Box sx={{ overflowY: 'auto', flex: '1 0 auto' }}>
      {/* <Box sx={{ m: '1.6rem' }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '12.25px 1.3rem',
          // border: '1px solid #F2F7FF',
          alignItems: 'center',
          borderRadius: '8px',
          boxShadow: '1px 4px 10px 0px #ADADAD29',
          width: '100%',
          cursor: 'pointer',
          backgroundColor: '#E6F6EC'
        }}>
          <Stack direction={"row"} spacing={1.5}>
            <img src={ForwardArrowIcon} style={{ height: 25, width: 25 }} alt="Forward Arrow Icon" />
            <Typography sx={{ color: "#239D56", fontStyle: "italic", fontWeight: 500 }}>
              Submit your application within the next 24 hours and save upto 50% on verification
            </Typography>
          </Stack>
        </Box>
      </Box> */}

      <Box sx={{ m: '1.6rem' }}>
        {txnInfo?.manualProfileRequired === false && (
          <VerificationTile
            label={isClientCareerMosaic() ? 'Upload Identity Document' : 'Identity'}
            iconComp={<UserIcon sx={{ height: '1.65rem', width: 'auto', color: 'primary.main' }} />}
            onClick={() => {
              if (!isDisclaimerChecked()) {
                return;
              }
              mixpanel.track(trackingEvents.profileVerification.events.opened, {});
              if (txnInfo?.questionnaire?.isRequired) {
                showPurposesDialog(() => {
                  if (profileVerified) {
                    navigateTo('/verification-info', { state: { showProfileData: true } });
                    return;
                  }
                  navigateTo('/profile-mode');
                });
              } else {
                if (profileVerified) {
                  navigateTo('/verification-info', { state: { showProfileData: true } });
                  return;
                }
                navigateTo('/profile-mode');
              }
            }}
            iconWrapperColor="primary.light"
            iconWrapperHeight="2.8rem"
            status={profileVerified ? TxnState.VERIFIED : undefined}
          />
        )}
      </Box>
      <Box sx={{ m: '1.6rem' }}>
        {showBasicDetails && (
          <VerificationTile
            label={import.meta.env.VITE_ORG == WES_ORG ? 'WES Reference Number' : 'Basic Details'}
            iconImgUrl={orgInfo?.imageUrl}
            disabled={profileNotVerified}
            onClick={() => {
              if (!isDisclaimerChecked()) {
                return;
              }
              showPurposesDialog(() => {
                navigateTo('/basic-details');
              });
            }}
            iconWrapperColor="primary.light"
            iconWrapperHeight="2.8rem"
            status={txnInfo?.state === TxnState.NOT_INITIALIZED ? undefined : TxnState.VERIFIED}
          />
        )}
      </Box>
      {Object.keys(verificationsByCategory ?? {})?.map((category) => (
        <Fragment key={category}>
          <Box
            sx={{
              ml: '2rem',
              mt: '2rem',
              display: 'flex',
              flexFlow: 'column',
              rowGap: '.55rem'
            }}>
            <Typography variant="body1" sx={{ fontWeight: '600', fontFamily: 'Poppins' }}>
              {`${category} ${category === INSUFF_CATEGORY ? '' : 'Check'}`}
            </Typography>
            {category === 'Education' && (
              <Typography variant="body2" sx={{ color: 'font.medium', fontWeight: '500' }}>
                You need to complete at least one step to continue
              </Typography>
            )}
          </Box>
          {verificationsByCategory?.[category]?.list?.map((row: VerificationState) => {
            const extractedNode = txnInfo?.priceList?.filter(
              (item: any) => item?.verificationName == row?.name
            );
            const displayPrice = extractedNode?.length > 0 ? extractedNode[0] : {};
            const mrp = displayPrice?.mrp;
            const discountedPrice = displayPrice?.price?.primary;
            const discountInfo =
              row.name == 'bgv-degree'
                ? `Save ${displayPrice?.discount?.secondary}% on additional degrees`
                : `Save upto ${displayPrice?.discount?.primary}%`;
            return (
              <Box sx={{ m: '1.4rem 1.6rem' }} key={row?.name}>
                <VerificationTile
                  label={row?.metadata?.label}
                  subtitle={getSubtitle(row?.name)}
                  iconImgUrl={row?.metadata?.iconUrl}
                  disabled={getDisabledForVerifications(row?.state, row?.markedAsSkip)}
                  onClick={() => {
                    if (!isDisclaimerChecked()) {
                      return;
                    }
                    showPurposesDialog(() => verificationTileOnClick(row));
                  }}
                  iconWrapperColor="primary.light"
                  iconWrapperHeight="2.8rem"
                  mrp={mrp}
                  discountedPrice={discountedPrice}
                  discountInfo={discountInfo}
                  status={getState(row?.state, row?.markedAsSkip) as TxnState}
                  sx={{
                    backgroundColor: category === INSUFF_CATEGORY ? 'error.light' : 'bg.white',
                    outline: category === INSUFF_CATEGORY ? '1px solid' : 'none',
                    outlineColor: 'error.main'
                  }}
                />
              </Box>
            );
          })}
        </Fragment>
      ))}
      {isClientCareerMosaic() && (
        <Box sx={{ m: '1.6rem' }}>
          <VerificationTile
            label={'University Selection'}
            iconComp={
              <AccountBalance sx={{ height: '1.65rem', width: 'auto', color: 'primary.main' }} />
            }
            onClick={() => {
              if (!isDisclaimerChecked()) {
                return;
              }
              navigateTo('/select-evaluation-partner', { state: { openEdit: true } });
            }}
            iconWrapperColor="primary.light"
            iconWrapperHeight="2.8rem"
            // status={evaluationPartnerVerified ? TxnState.SUBMITTED : undefined}
          />
        </Box>
      )}
    </Box>
  );
}

export default VerificationTiles;
